import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useTemplateManifest } from '../hooks/useTemplateManifest';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import FormField from '../components/FormField';
import HelpSidebar from '../components/HelpSidebar';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

export default function MenuEditor() {
  const { siteId, id } = useParams<{ siteId: string; id: string }>();
  const navigate = useNavigate();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'menu');
  const { addToast } = useToast();
  const { filterFields } = useTemplateManifest(site?.templateId);
  const { sendUpdate } = useEditorPreview('/menu');

  const isNew = id === 'new';
  const existingItem = isNew
    ? undefined
    : items.find((item) => getContentId(item.contentKey) === id);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const justCreatedRef = useRef(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawContentType = schema?.contentTypes.menu;
  const filteredContentType = rawContentType ? filterFields('menu', rawContentType) : undefined;
  const fields = filteredContentType?.fields || {};

  // For new items: dirty if any field has a non-empty value
  // For existing items: dirty if formData differs from originalData
  // Skip dirty check right after creation (navigating to edit URL)
  const isDirty = justCreatedRef.current
    ? false
    : isNew
      ? Object.values(formData).some((v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
      : JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  // Reset justCreated flag when id changes (after navigation completes)
  useEffect(() => {
    if (id !== 'new') {
      justCreatedRef.current = false;
    }
  }, [id]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    sendUpdate('menu', name, value);
  };

  const validate = (): boolean => {
    for (const [name, field] of Object.entries(fields)) {
      if (field.required) {
        const val = formData[name];
        if (!val || (typeof val === 'string' && !val.trim())) {
          addToast('error', `${field.label} is required`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!siteId || !validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        justCreatedRef.current = true;
        const created = await create(formData, items.length);
        addToast('success', 'Menu item created');
        const newId = getContentId(created.contentKey);
        navigate(`/sites/${siteId}/content/menu/${newId}`, { replace: true });
      } else if (id) {
        await update(id, formData);
        setOriginalData({ ...formData });
        addToast('success', 'Menu item updated');
      }
    } catch (err) {
      justCreatedRef.current = false;
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (!isNew && !existingItem && !loading) {
    return (
      <div className="page">
        <div className="error-state">
          <p>Menu item not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const help = pageHelp.menuItem;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-back">
          <button
            className="btn-icon"
            onClick={() => navigate(`/sites/${siteId}/content/menu`)}
            title="Back to menu"
          >
            ←
          </button>
          <h1>{isNew ? 'New Menu Item' : 'Edit Menu Item'}</h1>
        </div>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          <div className="editor-form">
            {Object.entries(fields).map(([name, fieldSchema]) => (
              <FormField
                key={name}
                name={name}
                schema={fieldSchema}
                value={formData[name]}
                onChange={handleChange}
                siteId={siteId!}
                cmsType="menu"
                cmsId={existingItem?.contentKey}
              />
            ))}
          </div>
        </div>
        <HelpSidebar title={help.title} description={help.description} tips={help.tips} />
      </div>
    </div>
  );
}
